import { useCallback } from 'react';
import {
  addDayOffset,
  DateString,
  DayOfWeek,
  daysBetween,
  getDayOfWeek,
  lastSundayDate,
  minDate,
  mostRecentSunday,
} from '../../../common/src/date_string.ts';
import { Household, Org, SongCompletionsThisWeek, SongSlug } from '../../../common/src/model.ts';
import { useCurrentDate } from '../util/use_current_date.tsx';
import { useHousehold } from './use_household.tsx';

// TODO(hewitt): once streak machinery is solid, reduce to something like 2-3 weeks to limit Household size
const singingPlanStreakSongRetentionDurationDays: number = 60; // number of days to retain singing plan streak data

export function useSingingPlanCompletions(): {
  isSongCompleted: (dateString: DateString, songSlug: SongSlug) => boolean;
  markSongCompleted: (dateString: DateString, songSlug: SongSlug) => void;
} {
  const { household, setHousehold } = useHousehold();
  const today = useCurrentDate();

  const isSongCompleted = useCallback(
    (date: DateString, songSlug: SongSlug) => {
      const { singingPlanStreak } = household ?? {};
      const { songCompletionsThisWeek } = singingPlanStreak ?? {};
      const songCompletionsToday = songCompletionsThisWeek?.[date] ?? [];
      return songCompletionsToday && songCompletionsToday.includes(songSlug);
    },
    [household],
  );

  const markSongCompleted = useCallback(
    async (date: DateString, songSlug: SongSlug) => {
      if (!household || isSongCompleted(date, songSlug)) {
        return;
      }
      const updatedHousehold: Household = {
        ...household,
        singingPlanStreak: {
          ...(household.singingPlanStreak ?? { asOfDate: lastSundayDate(today), countOfDays: 0 }),
          songCompletionsThisWeek: appendSongCompletion(
            date,
            songSlug,
            household.singingPlanStreak?.songCompletionsThisWeek,
          ),
        },
      };
      const countOfDays = getStreakInDays({ household: updatedHousehold, forDate: today });
      await setHousehold({
        ...updatedHousehold,
        singingPlanStreak: {
          ...updatedHousehold.singingPlanStreak,
          asOfDate: today,
          countOfDays,
        },
      });
    },
    [household, isSongCompleted, setHousehold, today],
  );

  return { isSongCompleted, markSongCompleted };
}

function appendSongCompletion(date: DateString, songSlug: SongSlug, songCompletionsThisWeek?: SongCompletionsThisWeek) {
  const retentionCutoff = addDayOffset(date, -singingPlanStreakSongRetentionDurationDays);
  return {
    ...Object.fromEntries(Object.entries(songCompletionsThisWeek ?? {}).filter(([date]) => date >= retentionCutoff)),
    [date]: [...(songCompletionsThisWeek?.[date] ?? []).filter((slug) => slug !== songSlug), songSlug],
  };
}

export function allSongsCompletedToday(
  household: Household | undefined,
  householdOrgs: Org[],
  today: DateString,
): boolean {
  if (!household) {
    return false;
  }
  const { singingPlanStreak } = household;
  if (!singingPlanStreak) {
    return false;
  }
  const { songCompletionsThisWeek } = singingPlanStreak;
  if (!songCompletionsThisWeek) {
    return false;
  }

  let planFound = false;
  for (const org of householdOrgs) {
    const dailyPlan = org.singingPlan?.days.find(({ date }) => date === today);
    // no singing plan = completed singing plan for this org
    if (!dailyPlan) {
      continue;
    }
    planFound = true;
    const songCompletionsToday = songCompletionsThisWeek[today];
    if (!songCompletionsToday) {
      return false;
    }
    for (const { slug } of dailyPlan.songs) {
      if (!songCompletionsToday.includes(slug)) {
        return false;
      }
    }
  }
  return planFound;
}

export function anySongInteractionsForDate(date: DateString, household?: Household): boolean {
  const songCompletions = household?.singingPlanStreak?.songCompletionsThisWeek?.[date];
  return !!songCompletions && songCompletions.length > 0;
}

export function getStreakInDays({ household, forDate }: { household?: Household; forDate: DateString }): number {
  if (!household) {
    return 0;
  }
  const { singingPlanStreak } = household;
  const lastSaturday = addDayOffset(mostRecentSunday(forDate), -1);
  const cutoffDate = minDate([lastSaturday, ...(singingPlanStreak ? [singingPlanStreak.asOfDate] : [])]);
  let completedDaysSinceCutoff = 0;
  for (let date = forDate; date > cutoffDate; date = addDayOffset(date, -1)) {
    // automatically count Sundays in streak because we assume everyone sings at church
    if (getDayOfWeek(date) !== DayOfWeek.Sunday && !anySongInteractionsForDate(date, household)) {
      // if the user has marked a song complete prior to the asOfDate, we need to account for the countOfDays
      const { asOfDate, countOfDays } = singingPlanStreak ?? {};
      const countOfDaysPriorToDate =
        asOfDate && countOfDays && asOfDate > date ? Math.max(0, countOfDays - daysBetween(date, asOfDate)) : 0;
      const completedDaysSinceDate = completedDaysSinceCutoff;
      return countOfDaysPriorToDate + completedDaysSinceDate;
    }
    completedDaysSinceCutoff += 1;
  }
  if (!singingPlanStreak) {
    return completedDaysSinceCutoff;
  }
  const { asOfDate, countOfDays } = singingPlanStreak;
  const countOfDaysPriorToCutoff = Math.max(0, countOfDays - daysBetween(cutoffDate, asOfDate));
  return countOfDaysPriorToCutoff + completedDaysSinceCutoff;
}
